import { useMemoizedFn, useRequest } from 'ahooks';
import { useEffect, useRef, useState } from 'react';
import { Sheet } from 'react-modal-sheet';
import EditCustom from '../EditCustom/index.jsx';
import { toast } from 'sonner';
import showErrMessage from '@/utils/showErrMessage.js';
import { hooks } from '@/store.js';
import type { IListItem } from '../../interface.js';
import useConfirm from '@/hooks/useConfirm/index.jsx';
import { planDetailEvent } from '../../utils/event.js';

const useEditCustom = ({
  getList,
}: {
  getList: () => IListItem[] | undefined;
}) => {
  const requestRefresh = useMemoizedFn(() => {
    planDetailEvent.emit('requestRefresh');
  });
  const [placeholder, setPlaceholder] = useState<null | ReturnType<
    typeof Sheet
  >>(null);
  const idRef = useRef('');

  const { runAsync: reorder } = useRequest(
    async (payload: 'up' | 'down' | 'end' | 'start', id?: string) => {
      const curId = id || idRef.current;

      // STEP: 获取下一个id
      const list = getList();
      if (!list) return;

      const idx = list.findIndex(
        (i) =>
          i.props &&
          'id' in (i.props as unknown as { id: string }) &&
          (i.props as { id: string }).id === curId
      );
      const currentIdx = list[idx].orderIdx;

      if (payload === 'end') {
        // STEP: 下一个DateTitleIdx
        let nextDateTitleIdx = list.findIndex(
          (i, index) => i.type === 'DayTitle' && index > idx
        );
        if (nextDateTitleIdx === -1) nextDateTitleIdx = list.length;

        const target = list.findLast(
          (i, index) =>
            !['Add', 'Divider', 'DayTitle'].includes(i.type) &&
            index < nextDateTitleIdx
        );

        const targetIdx = target?.orderIdx;
        if (!targetIdx || !currentIdx) {
          toast.warning('目标组件未设置排序位置');
          return;
        }

        await (
          await client.events.get(curId).resolved
        )?.set('orderIdx', targetIdx + 100);

        return;
      }

      if (payload === 'start') {
        let prevDateTitleIdx = list.findLastIndex(
          (i, index) => i.type === 'DayTitle' && index < idx
        );
        if (prevDateTitleIdx === -1) prevDateTitleIdx = 0;

        const target = list.find(
          (i, index) =>
            !['Add', 'Divider', 'DayTitle'].includes(i.type) &&
            index > prevDateTitleIdx
        );
        const targetIdx = target?.orderIdx;

        if (!targetIdx || !currentIdx) {
          toast.warning('目标组件未设置排序位置');
          return;
        }

        await (
          await client.events.get(curId).resolved
        )?.set('orderIdx', targetIdx - 100);

        return;
      }

      const curOrderIdx = list[idx]?.orderIdx;

      if (payload === 'up') {
        const preRecord = list[idx - 1];

        if (preRecord.orderIdx) {
          if (preRecord.props.id && curOrderIdx) {
            await (
              await client.events.get(preRecord.props.id).resolved
            )?.set('orderIdx', curOrderIdx);
          }

          await (
            await client.events.get(curId).resolved
          )?.set('orderIdx', preRecord.orderIdx);
        }
      } else if (payload === 'down') {
        const nextRecord = list[idx + 1];
        if (nextRecord.orderIdx) {
          if (nextRecord.props.id && curOrderIdx) {
            await (
              await client.events.get(nextRecord.props.id).resolved
            )?.set('orderIdx', curOrderIdx);
          }

          await (
            await client.events.get(curId).resolved
          )?.set('orderIdx', nextRecord.orderIdx);
        }
      }
    },
    {
      manual: true,
      onError: showErrMessage,
      onSuccess() {
        hide();
        requestRefresh();
        toast.success('操作成功');
      },
    }
  );

  const show = useMemoizedFn(({ id }: { id: string }) => {
    setIsOpen(true);
    idRef.current = id;
    setPlaceholder(
      <>
        <Sheet.Container
          className='mb-safe !left-3 !right-3 !w-auto !rounded-3xl  '
          style={{ boxShadow: 'rgba(0, 0, 0, 0.2) 0px -18px 50px -10px' }}
        >
          <Sheet.Header />
          <Sheet.Content
            style={{
              maxHeight: 'calc(100% - env(safe-area-inset-top) - 34px)',
            }}
          >
            <Sheet.Scroller>
              <EditCustom
                id={id}
                requestClose={showModal}
                requestReorder={reorder}
              />
            </Sheet.Scroller>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop
          onTap={(e) => {
            e.stopPropagation();
            e.preventDefault();
            // onClose();
            setIsOpen(false);
          }}
        />
      </>
    );
  });

  const hide = useMemoizedFn(() => {
    setIsOpen(false);
    idRef.current = '';
    setPlaceholder(null);
  });

  const client = hooks.useClient();
  const { loading: deleting, runAsync: runDelete } = useRequest(
    async () => {
      return await client.events.delete(idRef.current);
    },
    {
      manual: true,
      onError: showErrMessage,
      onSuccess() {
        hide();
        requestRefresh();
        toast.success('删除成功');
      },
    }
  );
  const { showModal } = useConfirm({
    isSubmitting: deleting,
    onSubmit: runDelete,
    title: '确定删除该活动吗?',
  });

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    planDetailEvent.on('reorder', reorder);
    return () => {
      planDetailEvent.off('reorder', reorder);
    };
  }, []);

  return {
    placeholder: (
      <Sheet
        isOpen={isOpen}
        onClose={hide}
        detent='content-height'
        disableScrollLocking
        style={{ zIndex: 50 }}
        // rootId='root'
      >
        {placeholder}
      </Sheet>
    ),
    show,
  };
};

export default useEditCustom;
